import { DIST_EPSILON } from '../constants';
import type { Vec3 } from '../vec';
import type { CollisionWorld, SolidEntity, TraceResult } from './types';

/** Axis-aligned solid box. `min`/`max` are mutable so moving boxes (bhop blocks) can be updated in place. */
export interface Box {
  min: Vec3;
  max: Vec3;
  entity: SolidEntity;
}

// The prototype tested axes in three.js order (x, y, z), which is Source (Y, Z, X).
// Ties between axes go to the first one tested, so keep that order.
const AXES = [1, 2, 0] as const;

const delta: Vec3 = [0, 0, 0];

/** Swept AABB against a list of boxes. Port of the prototype's trace(). */
export class BoxWorld implements CollisionWorld {
  readonly boxes: Box[] = [];

  add(min: Vec3, max: Vec3, entity: SolidEntity): Box {
    const box: Box = { min, max, entity };
    this.boxes.push(box);
    return box;
  }

  trace(
    start: Readonly<Vec3>,
    end: Readonly<Vec3>,
    mins: Readonly<Vec3>,
    maxs: Readonly<Vec3>,
    mask: number,
    out: TraceResult,
  ): TraceResult {
    delta[0] = end[0] - start[0];
    delta[1] = end[1] - start[1];
    delta[2] = end[2] - start[2];
    let best = 1;
    let axisHit = -1;
    let hitEntity: SolidEntity | null = null;
    let startSolid = false;
    let allSolid = false;
    const boxes = this.boxes;
    for (let k = 0; k < boxes.length; k++) {
      const b = boxes[k];
      if ((b.entity.contents & mask) === 0) continue;
      let tEnter = -Infinity;
      let tExit = Infinity;
      let axis = -1;
      let miss = false;
      for (let ai = 0; ai < 3; ai++) {
        const a = AXES[ai];
        // Minkowski-expand the box by the hull.
        const emin = b.min[a] - maxs[a];
        const emax = b.max[a] - mins[a];
        const s = start[a];
        const v = delta[a];
        if (Math.abs(v) < 1e-9) {
          if (s <= emin || s >= emax) {
            miss = true;
            break;
          }
          continue;
        }
        let t1 = (emin - s) / v;
        let t2 = (emax - s) / v;
        if (t1 > t2) {
          const tmp = t1;
          t1 = t2;
          t2 = tmp;
        }
        if (t1 > tEnter) {
          tEnter = t1;
          axis = a;
        }
        if (t2 < tExit) tExit = t2;
        if (tEnter > tExit) {
          miss = true;
          break;
        }
      }
      if (miss) continue;
      if (axis < 0) {
        // Not moving and inside the box on every axis.
        startSolid = allSolid = true;
        continue;
      }
      if (tEnter < 0 && tExit > 0) {
        startSolid = true;
        if (tExit >= 1) allSolid = true;
      }
      if (tEnter < -1e-4 || tEnter >= best || tExit <= 0) continue;
      best = Math.max(0, tEnter);
      axisHit = axis;
      hitEntity = b.entity;
    }

    out.startSolid = startSolid;
    out.allSolid = allSolid;
    if (axisHit < 0) {
      out.fraction = 1;
      out.endPos[0] = end[0];
      out.endPos[1] = end[1];
      out.endPos[2] = end[2];
      out.planeNormal[0] = out.planeNormal[1] = out.planeNormal[2] = 0;
      out.entity = null;
      out.contents = 0;
      return out;
    }
    const frac = Math.max(0, best - DIST_EPSILON / Math.abs(delta[axisHit]));
    out.fraction = frac;
    out.endPos[0] = start[0] + delta[0] * frac;
    out.endPos[1] = start[1] + delta[1] * frac;
    out.endPos[2] = start[2] + delta[2] * frac;
    out.planeNormal[0] = out.planeNormal[1] = out.planeNormal[2] = 0;
    out.planeNormal[axisHit] = delta[axisHit] > 0 ? -1 : 1;
    out.entity = hitEntity;
    out.contents = hitEntity ? hitEntity.contents : 0;
    return out;
  }

  testPosition(origin: Readonly<Vec3>, mins: Readonly<Vec3>, maxs: Readonly<Vec3>, mask: number): boolean {
    const boxes = this.boxes;
    for (let k = 0; k < boxes.length; k++) {
      const b = boxes[k];
      if ((b.entity.contents & mask) === 0) continue;
      if (
        origin[0] + mins[0] < b.max[0] &&
        origin[0] + maxs[0] > b.min[0] &&
        origin[1] + mins[1] < b.max[1] &&
        origin[1] + maxs[1] > b.min[1] &&
        origin[2] + mins[2] < b.max[2] &&
        origin[2] + maxs[2] > b.min[2]
      )
        return true;
    }
    return false;
  }
}
